import React from 'react';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/compat/router';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUI from 'src/hooks/use-ui';
import { WhoopsContent, WhoopsReason } from 'components/whoops';

import { KIOSK_CONCIERGE_ROLLOUT_FLAG } from '@dutchie/common/utils/concierge-rollout';
import { useLatchedFlag } from 'src/hooks/use-latched-flag';
import { ConciergeHost } from './concierge-host';
import { isValidMenuVariant } from './bridge/utils';
import { isKioskConciergeSettingOn } from './use-is-kiosk-concierge-enabled';
import { useIsLdContextForDispensary, useIsLdIdentifiedForDispensary } from './use-is-ld-identified-for-dispensary';

/**
 * Renders the concierge experience for a dispensary. The gate is variant-aware
 * (ECOMM-12587): kiosk routes require the `ecomm.concierge.kiosk.rollout` LD
 * flag AND `conciergeChannels.kiosk` (fail closed, see
 * useIsKioskConciergeEnabled); storefront and embedded routes read only
 * `conciergeChannels.web`. Kiosk and web concierge are separate
 * products that enable independently.
 */
export const ConciergePage = observer((): JSX.Element | null => {
  const router = useRouter();
  const UI = useUI();
  const { dispensary, loading: dispensaryLoading } = useDispensary();
  // Latched for this dispensary, and only once LD is identified with it: a late LD identify that
  // briefly drops the flag cannot tear down the open page, and another store's still-live flag
  // cannot open it. The page remounts on navigation, so the kill switch applies on the next one.
  const isLdContextForDispensary = useIsLdContextForDispensary(dispensary?.id);
  const isKioskFlagLatched = useLatchedFlag(
    KIOSK_CONCIERGE_ROLLOUT_FLAG,
    dispensary?.id ?? null,
    isLdContextForDispensary
  );
  const isKioskConciergeEnabled = isKioskFlagLatched && isKioskConciergeSettingOn(dispensary);
  const isLdIdentifiedForDispensary = useIsLdIdentifiedForDispensary(dispensary?.id);

  const isDispensaryEnabled = UI.isKiosk ? isKioskConciergeEnabled : Boolean(dispensary?.conciergeChannels?.web);

  // Wait for router params + the dispensary fetch to settle. Once both are
  // resolved, the variant's enablement flag is authoritative. Also wait for UI.variant: UI.href is synced
  // from the router in a post-render effect, so after client nav (e.g. the SSO callback) it is stale.
  if (!router?.isReady || dispensaryLoading || !dispensary || !isValidMenuVariant(UI.variant)) {
    return null;
  }

  // A false kiosk flag is not authoritative until LD has evaluated it for this
  // dispensary (or the bounded wait expires), so wait instead of flashing a 404.
  if (UI.isKiosk && !isDispensaryEnabled && !isLdIdentifiedForDispensary) {
    return null;
  }

  if (!isDispensaryEnabled) {
    return <WhoopsContent reason={WhoopsReason.pageNotFound} />;
  }

  return <ConciergeHost />;
});
